import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Gift, Wallet, Send, Download, 
  ChevronRight, Clock, Check, X, Snowflake,
  AlertCircle, Wine, Utensils, Sparkles, ShoppingBag,
  Plane, Ticket, PartyPopper, Heart, Package, User,
  Apple
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Gift category icons mapping
const CATEGORY_ICONS = {
  drinks: Wine,
  dinner: Utensils,
  spa_wellness: Sparkles,
  shopping: ShoppingBag,
  travel: Plane,
  entertainment: Ticket,
  celebration: PartyPopper,
  just_because: Heart,
  custom: Gift,
  flexible: Package
};

// Gift state display config
const STATE_CONFIG = {
  created: { label: 'Processing', color: '#FFA500', icon: Clock },
  payment_pending: { label: 'Payment Pending', color: '#FFA500', icon: Clock },
  funded: { label: 'Funded', color: '#28A745', icon: Check },
  sent: { label: 'Sent', color: '#17A2B8', icon: Send },
  delivered: { label: 'Delivered', color: '#17A2B8', icon: Check },
  accepted: { label: 'Accepted', color: '#28A745', icon: Check },
  active: { label: 'Active', color: '#28A745', icon: Check },
  partially_redeemed: { label: 'Partially Used', color: '#FFA500', icon: Download },
  fully_redeemed: { label: 'Fully Used', color: '#6C757D', icon: Check },
  frozen: { label: 'Frozen', color: '#DC3545', icon: Snowflake },
  disputed: { label: 'Disputed', color: '#DC3545', icon: AlertCircle },
  cancelled: { label: 'Cancelled', color: '#6C757D', icon: X },
  refunded: { label: 'Refunded', color: '#6C757D', icon: Check },
  declined: { label: 'Declined', color: '#DC3545', icon: X }
};

const GiftWalletPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('received'); // received, sent
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDark, setIsDark] = useState(true);

  useEffect(() => {
    const checkTheme = () => {
      const saved = localStorage.getItem('nearU_theme');
      setIsDark(saved !== 'light');
    };
    checkTheme();
    const interval = setInterval(checkTheme, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchWallet();
  }, []);

  const fetchWallet = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gifts/wallet`);
      const data = await res.json();
      setWallet(data);
    } catch (err) {
      console.error('Failed to load wallet:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const colors = {
    background: isDark ? '#0D0D0D' : '#FFFFFF',
    cardBg: isDark ? 'rgba(28, 28, 28, 0.9)' : 'rgba(255, 255, 255, 0.95)',
    text: isDark ? '#F4E7C3' : '#1A1A1A',
    textMuted: isDark ? 'rgba(244, 231, 195, 0.7)' : 'rgba(26, 26, 26, 0.6)',
    border: isDark ? 'rgba(212, 175, 55, 0.3)' : 'rgba(165, 124, 51, 0.2)',
    accent: '#D4AF37',
    accentDark: '#A57C33'
  };

  const renderGiftCard = (gift, isReceived = true) => {
    const CategoryIcon = CATEGORY_ICONS[gift.category] || Gift;
    const stateConfig = STATE_CONFIG[gift.state] || STATE_CONFIG.created;
    const StateIcon = stateConfig.icon;
    const balance = gift.recipient_available_balance_cents || gift.gross_gift_amount_cents;
    const isActive = ['active', 'partially_redeemed', 'sent', 'accepted'].includes(gift.state);

    return (
      <div
        key={gift.id}
        onClick={() => navigate(`/gifts/${gift.id}`)}
        style={{
          background: colors.cardBg,
          borderRadius: '16px',
          padding: '16px',
          border: `1px solid ${colors.border}`,
          marginBottom: '12px',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
          {/* Category Icon */}
          <div style={{
            width: '52px',
            height: '52px',
            borderRadius: '14px',
            background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <CategoryIcon size={26} color="#FFF" />
          </div>

          {/* Gift Info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
              <div style={{ 
                color: colors.text, 
                fontSize: '16px', 
                fontWeight: '600',
                textTransform: 'capitalize'
              }}>
                {gift.category.replace('_', ' ')} Gift
              </div>
              <div style={{ 
                color: isActive ? colors.accent : colors.text, 
                fontSize: '18px', 
                fontWeight: '700'
              }}>
                {formatCurrency(balance)}
              </div>
            </div>

            <div style={{ 
              color: colors.textMuted, 
              fontSize: '13px',
              marginBottom: '8px'
            }}>
              {isReceived ? (
                <>From: {gift.sender_name}</>
              ) : (
                <>To: {gift.recipient_name}</>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                padding: '4px 10px',
                background: `${stateConfig.color}20`,
                borderRadius: '12px'
              }}>
                <StateIcon size={12} color={stateConfig.color} />
                <span style={{ 
                  color: stateConfig.color, 
                  fontSize: '11px', 
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {stateConfig.label}
                </span>
              </div>
              <span style={{ color: colors.textMuted, fontSize: '12px' }}>
                {formatDate(gift.created_at)}
              </span>
            </div>

            {/* Message Preview */}
            {gift.message && (
              <div style={{
                marginTop: '10px',
                padding: '10px 12px',
                background: isDark ? 'rgba(212, 175, 55, 0.08)' : 'rgba(212, 175, 55, 0.05)',
                borderRadius: '8px',
                color: colors.textMuted,
                fontSize: '12px',
                fontStyle: 'italic',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                "{gift.message}"
              </div>
            )}
          </div>

          <ChevronRight size={20} color={colors.textMuted} style={{ flexShrink: 0 }} />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ color: colors.accent }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.background,
      paddingBottom: '100px'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '16px',
        borderBottom: `1px solid ${colors.border}`
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none',
            border: 'none',
            padding: '8px',
            cursor: 'pointer'
          }}
        >
          <ArrowLeft size={24} color={colors.text} />
        </button>
        <h1 style={{
          flex: 1,
          textAlign: 'center',
          color: colors.text,
          fontSize: '18px',
          fontWeight: '600',
          fontFamily: 'Cinzel, serif'
        }}>
          Gift Wallet
        </h1>
        <div style={{ width: '40px' }} />
      </div>

      {/* Balance Card */}
      <div style={{ padding: '16px' }}>
        <div style={{
          background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2), rgba(165, 124, 51, 0.1))',
          borderRadius: '20px',
          padding: '24px',
          border: `2px solid ${colors.accent}`,
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #D4AF37, #A57C33)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Wallet size={24} color="#FFF" />
            </div>
            <div>
              <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '2px' }}>
                Available Balance
              </div>
              <div style={{ 
                color: colors.accent, 
                fontSize: '32px', 
                fontWeight: '700',
                fontFamily: 'Cinzel, serif'
              }}>
                {wallet?.total_balance_display || '$0.00'}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => navigate('/gifts/browse-members')}
              data-testid="send-gift-btn"
              style={{
                flex: 1,
                padding: '14px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #D4AF37, #A57C33)',
                color: '#1A1A1A',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Send size={18} />
              Send Gift
            </button>
            <button
              style={{
                padding: '14px 20px',
                borderRadius: '12px',
                border: `1px solid ${colors.border}`,
                background: 'transparent',
                color: colors.text,
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Apple size={18} />
              Add to Wallet
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
          borderRadius: '12px',
          padding: '4px',
          marginBottom: '20px'
        }}>
          {[
            { id: 'received', label: 'Received', count: wallet?.gifts_received?.length || 0 },
            { id: 'sent', label: 'Sent', count: wallet?.gifts_sent?.length || 0 }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '10px',
                border: 'none',
                background: activeTab === tab.id 
                  ? 'linear-gradient(135deg, #D4AF37, #A57C33)'
                  : 'transparent',
                color: activeTab === tab.id ? '#1A1A1A' : colors.textMuted,
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Gift List */}
        <div>
          {activeTab === 'received' ? (
            wallet?.gifts_received?.length > 0 ? (
              wallet.gifts_received.map(gift => renderGiftCard(gift, true))
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '48px 24px',
                color: colors.textMuted
              }}>
                <Gift size={48} color={colors.border} style={{ marginBottom: '16px' }} />
                <p style={{ fontSize: '16px', marginBottom: '8px' }}>No gifts received yet</p>
                <p style={{ fontSize: '13px' }}>When someone sends you a gift, it will appear here</p>
              </div>
            )
          ) : (
            wallet?.gifts_sent?.length > 0 ? (
              wallet.gifts_sent.map(gift => renderGiftCard(gift, false))
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '48px 24px',
                color: colors.textMuted
              }}>
                <Send size={48} color={colors.border} style={{ marginBottom: '16px' }} />
                <p style={{ fontSize: '16px', marginBottom: '8px' }}>No gifts sent yet</p>
                <p style={{ fontSize: '13px', marginBottom: '20px' }}>Share the joy by sending a gift to someone special</p>
                <button
                  onClick={() => navigate('/browse-members')}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #D4AF37, #A57C33)',
                    color: '#1A1A1A',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Browse Members
                </button>
              </div>
            )
          )}
        </div>
      </div>

      {/* Legal Notice */}
      <div style={{
        padding: '16px',
        marginTop: '24px'
      }}>
        <div style={{
          padding: '12px 16px',
          background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          borderRadius: '8px'
        }}>
          <p style={{ 
            color: colors.textMuted, 
            fontSize: '10px',
            lineHeight: '1.5',
            margin: 0,
            textAlign: 'center'
          }}>
            Financial functionality is subject to final sponsor-bank, issuing-program, 
            regulatory and legal approval. Gift balances are for eligible purchases only. 
            No cash withdrawal or P2P transfer available.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GiftWalletPage;
